// styles.js
import styled, { css } from 'styled-components';

/* ===== Layout / Frame ===== */
export const Page = styled.div`
  min-height: 100vh;
  background: #f9fafb; /* gray-50 */
`;

export const Container = styled.div`
  max-width: 64rem; /* ~max-w-4xl */
  margin: 0 auto;
  padding: 1rem 0;

  @media (min-width: 640px) {
    padding: 1.5rem; /* sm:p-6 */
  }
`;

/* ===== Cards ===== */
export const Card = styled.div`
  background: #ffffff;
  margin-bottom: 1.5rem;
  border-radius: 8px;

  &.soft {
    background: #f3f4f6; /* gray-100 */
    border-style: dashed;
    border-width: 2px;
    border-color: #d1d5db; /* gray-300 */
  }

  &.green-dark {
    background: #005d1f; /* green-800 */
    color: #fff;
    border: 0;
  }
`;

export const CardHeader = styled.div`
  padding: 1rem 0;
  background: #ffffff;
`;

export const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #231f20;
  font-weight: 600;
  font-size: 20px;
`;

export const CardBody = styled.div`
  padding: 1rem 0;
`;

/* ===== Buttons ===== */
export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;
  font-weight: 500;
  color: #1f2937; /* gray-800 */
  background: #d2fa52;
  border: 0;
  cursor: pointer;

  @media (min-width: 640px) {
    margin-bottom: 2rem;
  }
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  border-radius: 9999px;
  font-weight: 600;
  border: 0;
  cursor: pointer;

  ${({ disabled }) =>
    disabled
      ? css`
          background: #d1d5db;
          color: #6b7280;
          cursor: not-allowed;
        `
      : css`
          background: #005d1f;
          color: #fff;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
          transition: box-shadow 0.2s ease;
          &:hover {
            box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.15),
              0 6px 8px -3px rgba(0, 0, 0, 0.06);
          }
        `}

  width: 100%;
  @media (min-width: 640px) {
    width: auto;
    padding: 1rem 3rem;
    font-size: 1.125rem;
  }
`;

export const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: #fff;
  border: 1px solid #d1d5db; /* gray-300 */
  color: #374151; /* gray-700 */
  cursor: pointer;

  &.orange {
    border-color: #fdba74; /* orange-300 */
    color: #b45309; /* orange-700 */
    &:hover {
      background: #fff7ed;
    } /* orange-50 */
  }
  &.purple {
    border-color: #d8b4fe; /* purple-300 */
    color: #7e22ce; /* purple-700 */
    &:hover {
      background: #faf5ff;
    } /* purple-50 */
  }
  &.green {
    border-color: #86efac; /* green-300 */
    color: #166534; /* green-700 */
    &:hover {
      background: #f0fdf4;
    } /* green-50 */
  }
`;

/* ===== Headings / Center blocks ===== */
export const CenterHeader = styled.div`
  text-align: center;
  margin-top: 32px;
  margin-bottom: 32px;

  h1 {
    margin: 0 0 0.5rem 0;
    color: #005d1f;
    font-weight: 600;
    font-size: 24px;
  }
  p {
    margin: 0;
    color: #231f20;
    font-size: 16px;
    font-weight: 400;
    b {
      font-weight: 600;
    }
  }
`;

export const BalanceHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    font-weight: 500;
    font-size: 16px;
  }
`;
export const BalanceSub = styled.p`
  margin: 0 0 0.5rem 0;
  color: #d1fae5; /* green-100 */
  font-size: 1rem;
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;
export const BalanceValue = styled.p`
  margin: 0;
  font-weight: 700;
  font-size: 32px;
`;
export const BalanceFee = styled.p`
  margin: 0.5rem 0 20px 0;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
`;

/* ===== Form ===== */
export const Field = styled.div``;

export const Label = styled.label`
  display: inline-block;
  color: #231f20;
  font-weight: 500;
  font-size: 16px;
  margin-bottom: 8px;
`;

export const TextInput = styled.input`
  width: 100%;
  margin-top: 0;
  border-radius: 8px;
  border: 1px solid ${({ $error }) => ($error ? '#d60000' : '#231f20')};
  padding: 12px 16px;
  outline: none;
  color: #231f20;
  font-size: 16px;
  font-weight: 400;

  &:focus {
    border-color: #008427;
    box-shadow: none;
  }

  &::placeholder {
    color: #6e6e6e;
  }

  &:disabled {
    cursor: not-allowed;
    background: #f1f1f1;
    border: 1px solid #d2d2d2;
  }
`;

export const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const Select = styled.select`
  width: 100%;
  margin-top: 0;
  border-radius: 8px;
  border: 1px solid ${({ $error }) => ($error ? '#d60000' : '#231f20')};
  padding: 13px 40px 13px 16px;
  outline: none;
  color: #231f20;
  background-color: #fff;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 400;

  &:focus {
    border-color: #008427;
    box-shadow: none;
  }

  &::placeholder {
    color: #6e6e6e;
  }
`;

export const Chevron = styled.span`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.2s ease-in-out;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    stroke: #231f20;
  }
`;

export const Help = styled.p`
  margin: 0.25rem 0 0 0;
  color: #4f4c4d;
  font-size: 14px;
  font-weight: 500;
`;

export const FormHelp = styled.p`
  margin: 16px 0 0 0;
  color: #231f20;
  font-size: 14px;
  font-weight: 400;
`;

export const ErrorRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.5rem;
  color: #ef4444; /* red-500 */
  font-size: 14px;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f0fdf4; /* green-50 */
  border-radius: 8px;
  color: #166534; /* green-700 */
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  max-height: ${({ $visible }) => ($visible ? '100px' : '0')};
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  padding: ${({ $visible }) => ($visible ? '0.75rem 1rem' : '0 1rem')};
  transition: max-height 300ms ease-in-out, opacity 300ms ease-in-out, padding 300ms ease-in-out;

  svg {
    flex-shrink: 0;
  }
`;

/* ===== Grids ===== */
export const GridMd2 = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

/* ===== Collapsible Section ===== */
export const CollapsibleSection = styled.div`
  overflow: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  max-height: ${({ $visible }) => ($visible ? '500px' : '0')};
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  transition: max-height 300ms ease-in-out, opacity 300ms ease-in-out;
`;

export const GridSm3 = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

/* ===== Alerts ===== */
export const AlertCard = styled(Card)`
  ${({ $variant }) =>
    $variant === 'orange' &&
    css`
      background: #fff7ed; /* orange-50 */
      border: 2px solid #fdba74; /* orange-300 */
      color: #9a3412; /* orange-800 */
    `}
  ${({ $variant }) =>
    $variant === 'purple' &&
    css`
      background: #faf5ff; /* purple-50 */
      border: 2px solid #d8b4fe; /* purple-300 */
      color: #6b21a8; /* purple-800 */
    `}
`;
export const AlertRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

/* ===== Demo box ===== */
export const SectionCenter = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  h3 {
    margin: 0 0 0.5rem 0;
    color: #374151;
    font-weight: 600;
  }
  p {
    margin: 0;
    color: #4b5563;
    font-size: 0.875rem;
  }
`;

/* (Optional) dashed container look */
export const DemoBox = styled(Card)`
  background: #f3f4f6; /* gray-100 */
  border: 2px dashed #d1d5db; /* gray-300 */
`;
